import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  Image,
  Input,
  List,
  message,
  Modal,
  Pagination,
  Radio,
  Row,
  Slider,
  Spin,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CopyOutlined,
  LinkOutlined,
  PictureOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import ToolPageHeader from '../../components/ToolPageHeader';
import { copyToClipboardAsync, getStoredWatermarkText, saveWatermarkText } from '../../helpers/clipboard';
import { getId } from '../../helpers/file';
import {
  buildTextWatermarkUrl,
  TEXT_WATERMARK_MAX_LENGTH,
  TextWatermarkConfig,
  TextWatermarkMode,
} from '../../helpers/watermark';
import { useTranslation } from '../../hooks/useTranslation';
import './index.css';

const { Title, Text } = Typography;
const { Search } = Input;

interface UploadRecord {
  id: number | string;
  fileName: string;
  url: string;
  rawUrl?: string;
  contentType: string;
  time?: string;
  size?: string;
  uid?: string;
}

const isImageRecord = (record: UploadRecord): boolean => {
  return Boolean(record.contentType && record.contentType.startsWith('image/'));
};

const getRecordId = (record: UploadRecord): string => String(record.id);

const ImageWatermark: React.FC = () => {
  const { t } = useTranslation();
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<UploadRecord | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shortLinkLoading, setShortLinkLoading] = useState(false);
  const [generatedShortUrl, setGeneratedShortUrl] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [watermarkText, setWatermarkText] = useState(() => getStoredWatermarkText());
  const [fontSize, setFontSize] = useState(20);
  const [opacity, setOpacity] = useState(50);
  const [mode, setMode] = useState<TextWatermarkMode>('southeast');

  const trimmedText = watermarkText.trim();
  const watermarkConfig: TextWatermarkConfig = {
    text: trimmedText,
    fontSize,
    opacity,
    mode,
  };
  const watermarkedUrl = selectedRecord && trimmedText
    ? buildTextWatermarkUrl(selectedRecord.url, watermarkConfig)
    : '';
  const previewUrl = selectedRecord && trimmedText ? watermarkedUrl : selectedRecord?.url;

  const loadHistory = async (
    currentPage: number = page,
    searchKeyword: string = keyword,
    currentPageSize: number = pageSize,
  ) => {
    setLoading(true);
    try {
      const response = await fetch(
        `/img/history?page=${currentPage}&pageSize=${currentPageSize}&keyword=${encodeURIComponent(searchKeyword)}&uid=${getId() || ''}`,
        {
          method: 'GET',
          credentials: 'include',
        },
      );
      const result = await response.json();
      const nextRecords = Array.isArray(result.data)
        ? result.data.filter((record: UploadRecord) => isImageRecord(record))
        : [];

      setRecords(nextRecords);
      setTotal(Number(result.total || 0));
    } catch (error) {
      console.error('加载图片历史失败:', error);
      message.error(t('imageProcessing.tools.watermark.networkError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(1, '', pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setGeneratedShortUrl('');
  }, [selectedRecord, watermarkText, fontSize, opacity, mode]);

  const validateBeforeGenerate = () => {
    if (!trimmedText) {
      message.warning(t('imageProcessing.tools.watermark.textRequired'));
      return false;
    }
    if (trimmedText.length > TEXT_WATERMARK_MAX_LENGTH) {
      message.error(t('imageProcessing.tools.watermark.textTooLong', { count: TEXT_WATERMARK_MAX_LENGTH }));
      return false;
    }
    if (!selectedRecord) {
      message.warning(t('imageProcessing.tools.watermark.selectImageFirst'));
      return false;
    }
    return true;
  };

  const createShortUrl = async () => {
    if (!validateBeforeGenerate()) {
      return '';
    }

    saveWatermarkText(trimmedText);
    setShortLinkLoading(true);
    try {
      const response = await fetch(`/u/save?url=${encodeURIComponent(watermarkedUrl)}`, {
        method: 'POST',
      });
      const json = await response.json();
      if (json?.shortId && typeof json.fullShortUrl === 'string' && json.fullShortUrl.trim()) {
        const shortUrl = json.fullShortUrl.trim();
        setGeneratedShortUrl(shortUrl);
        return shortUrl;
      }
      throw new Error(t('imageProcessing.tools.watermark.shortLinkGenerateFailed'));
    } catch (error) {
      console.error('生成水印短链接失败:', error);
      message.error(t('imageProcessing.tools.watermark.shortLinkGenerateFailed'));
      return '';
    } finally {
      setShortLinkLoading(false);
    }
  };

  const handleCopyShortLink = async () => {
    const shortUrl = generatedShortUrl || await createShortUrl();
    if (!shortUrl) {
      return;
    }

    await copyToClipboardAsync(shortUrl, t('imageProcessing.tools.watermark.shortLinkCopied'));
  };

  const handleCopyMarkdown = async () => {
    if (!selectedRecord) {
      message.warning(t('imageProcessing.tools.watermark.selectImageFirst'));
      return;
    }

    const shortUrl = generatedShortUrl || await createShortUrl();
    if (!shortUrl) {
      return;
    }

    await copyToClipboardAsync(
      `![${selectedRecord.fileName}](${shortUrl})`,
      t('imageProcessing.tools.watermark.markdownCopied'),
    );
  };

  const handleSearch = (value: string) => {
    setKeyword(value);
    setPage(1);
    loadHistory(1, value, pageSize);
  };

  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    setPage(nextPage);
    setPageSize(nextPageSize);
    loadHistory(nextPage, keyword, nextPageSize);
  };

  const handleSelectRecord = (record: UploadRecord) => {
    setSelectedRecord(record);
    setSelectorOpen(false);
  };

  return (
    <div className="image-watermark-page">
      <Card className="image-watermark-card">
        <ToolPageHeader
          backTo="/image-processing"
          backText={t('imageProcessing.tools.watermark.backToImageProcessing')}
          title={t('imageProcessing.tools.watermark.pageTitle')}
          subtitle={t('imageProcessing.tools.watermark.pageSubtitle')}
          icon={<PictureOutlined />}
          extra={<Tag color="cyan">{t('imageProcessing.tools.watermark.tag1')}</Tag>}
        />

        <Row gutter={[24, 24]} className="image-watermark-workbench">
          <Col xs={24} lg={14}>
            <Card className="watermark-preview-panel" bordered={false}>
              <div className="panel-heading">
                <div>
                  <Text className="panel-eyebrow">01 / INPUT</Text>
                  <Title level={3}>{t('imageProcessing.tools.watermark.previewTitle')}</Title>
                </div>
                <Button icon={selectedRecord ? <ReloadOutlined /> : <PictureOutlined />} onClick={() => setSelectorOpen(true)}>
                  {selectedRecord ? t('imageProcessing.tools.watermark.changeImage') : t('imageProcessing.tools.watermark.selectImage')}
                </Button>
              </div>

              {selectedRecord && previewUrl ? (
                <div className="watermark-preview-card">
                  <Image src={previewUrl} alt={selectedRecord.fileName} preview={{ src: previewUrl }} />
                  <div className="watermark-file-meta">
                    <div>
                      <Text strong>{selectedRecord.fileName}</Text>
                      <div className="watermark-file-detail">
                        {selectedRecord.size || '-'} · {selectedRecord.contentType || '-'}
                      </div>
                    </div>
                    <Tag color={mode === 'tile' ? 'blue' : 'green'}>
                      {mode === 'tile' ? t('imageProcessing.tools.watermark.modeTile') : t('imageProcessing.tools.watermark.modeSoutheast')}
                    </Tag>
                  </div>
                </div>
              ) : (
                <button type="button" className="watermark-empty-selector" onClick={() => setSelectorOpen(true)}>
                  <PictureOutlined />
                  <span>{t('imageProcessing.tools.watermark.selectImageHint')}</span>
                  <Text type="secondary">{t('imageProcessing.tools.watermark.selectImageSubHint')}</Text>
                </button>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={10}>
            <Card className="watermark-settings-panel" bordered={false}>
              <div className="panel-heading compact">
                <div>
                  <Text className="panel-eyebrow">02 / OUTPUT</Text>
                  <Title level={3}>{t('imageProcessing.tools.watermark.configTitle')}</Title>
                </div>
              </div>

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.watermark.textLabel')}</Text>
                  <Text type="secondary">0-{TEXT_WATERMARK_MAX_LENGTH}</Text>
                </div>
                <Input
                  value={watermarkText}
                  maxLength={TEXT_WATERMARK_MAX_LENGTH}
                  showCount
                  placeholder={t('imageProcessing.tools.watermark.textPlaceholder')}
                  onChange={(event) => setWatermarkText(event.target.value)}
                />
              </div>

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.watermark.modeLabel')}</Text>
                </div>
                <Radio.Group value={mode} onChange={(event) => setMode(event.target.value)} buttonStyle="solid" className="watermark-mode-group">
                  <Radio.Button value="southeast">{t('imageProcessing.tools.watermark.modeSoutheast')}</Radio.Button>
                  <Radio.Button value="tile">{t('imageProcessing.tools.watermark.modeTile')}</Radio.Button>
                </Radio.Group>
              </div>

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.watermark.fontSizeLabel')}</Text>
                  <Text type="secondary">{fontSize}px</Text>
                </div>
                <Slider min={12} max={72} value={fontSize} onChange={setFontSize} />
              </div>

              <div className="setting-block">
                <div className="setting-label">
                  <Text strong>{t('imageProcessing.tools.watermark.opacityLabel')}</Text>
                  <Text type="secondary">{opacity}%</Text>
                </div>
                <Slider min={10} max={100} value={opacity} onChange={setOpacity} />
              </div>

              <div className="watermark-readout">
                <span>{fontSize}px</span>
                <span>{opacity}%</span>
                <span>{mode === 'tile' ? t('imageProcessing.tools.watermark.modeTile') : t('imageProcessing.tools.watermark.modeSoutheast')}</span>
              </div>

              {generatedShortUrl && (
                <div className="watermark-short-result">
                  <Text type="secondary">{t('imageProcessing.tools.watermark.shortLinkResult')}</Text>
                  <a href={generatedShortUrl} target="_blank" rel="noopener noreferrer">{generatedShortUrl}</a>
                </div>
              )}

              <Button
                type="primary"
                block
                size="large"
                icon={<LinkOutlined />}
                loading={shortLinkLoading}
                onClick={handleCopyShortLink}
                className="watermark-primary-button"
              >
                {t('imageProcessing.tools.watermark.copyShortLink')}
              </Button>

              <Button
                block
                icon={<CopyOutlined />}
                loading={shortLinkLoading}
                onClick={handleCopyMarkdown}
                className="watermark-secondary-button"
              >
                {t('imageProcessing.tools.watermark.copyMarkdown')}
              </Button>
            </Card>
          </Col>
        </Row>

        <Modal
          open={selectorOpen}
          width={820}
          footer={null}
          onCancel={() => setSelectorOpen(false)}
          title={t('imageProcessing.tools.watermark.selectorTitle')}
          className="watermark-selector-modal"
        >
          <div className="watermark-selector-toolbar">
            <Search
              allowClear
              placeholder={t('imageProcessing.tools.watermark.searchPlaceholder')}
              onSearch={handleSearch}
            />
          </div>

          <Spin spinning={loading}>
            {records.length > 0 ? (
              <List
                className="watermark-selector-list"
                dataSource={records}
                renderItem={(record) => {
                  const selected = selectedRecord ? getRecordId(selectedRecord) === getRecordId(record) : false;
                  return (
                    <List.Item
                      className={`watermark-selector-row ${selected ? 'selected' : ''}`}
                      onClick={() => handleSelectRecord(record)}
                    >
                      <div className="selector-row-thumb">
                        <img src={record.url} alt={record.fileName} loading="lazy" />
                      </div>
                      <div className="selector-row-main">
                        <Text strong ellipsis>{record.fileName}</Text>
                        <Text type="secondary">{record.size || '-'} · {record.contentType || '-'}</Text>
                      </div>
                      {selected && <CheckCircleOutlined className="selector-row-check" />}
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty description={t('imageProcessing.tools.watermark.noImages')} />
            )}
          </Spin>

          <div className="watermark-pagination">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={total}
              showSizeChanger
              showTotal={(value) => t('imageProcessing.tools.watermark.paginationTotal', { total: value })}
              onChange={handlePageChange}
            />
          </div>
        </Modal>
      </Card>
    </div>
  );
};

export default ImageWatermark;
